import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const navLinks = [
  { to: '/ozellikler', label: { tr: 'Özellikler', en: 'Features' } },
  { to: '/fiyatlandirma', label: { tr: 'Fiyatlandırma', en: 'Pricing' } },
  { to: '/iletisim', label: { tr: 'İletişim', en: 'Contact' } },
];

const STR = {
  tr: {
    login: 'Giriş Yap', signup: 'Ücretsiz Başla', tagline: 'Otel operasyonlarını tek platformda yöneten, yeni nesil bulut tabanlı SaaS işletim sistemi.',
    product: 'Ürün', features: 'Özellikler', pricing: 'Fiyatlandırma', freeDemo: 'Ücretsiz Demo',
    legal: 'Yasal', kvkk: 'KVKK Aydınlatma Metni', privacy: 'Gizlilik Politikası', distance: 'Mesafeli Satış Sözleşmesi',
    refund: 'İptal & İade Koşulları', cookie: 'Çerez Politikası', contact: 'İletişim', reach: 'Bize Ulaşın', rights: 'Tüm hakları saklıdır.',
    poweredBy: 'Destekleyen',
  },
  en: {
    login: 'Log In', signup: 'Get Started Free', tagline: 'A next-generation cloud SaaS operating system that runs hotel operations on a single platform.',
    product: 'Product', features: 'Features', pricing: 'Pricing', freeDemo: 'Free Demo',
    legal: 'Legal', kvkk: 'GDPR Disclosure', privacy: 'Privacy Policy', distance: 'Distance Sales Agreement',
    refund: 'Cancellation & Refund Terms', cookie: 'Cookie Policy', contact: 'Contact', reach: 'Reach Us', rights: 'All rights reserved.',
    poweredBy: 'Powered by',
  },
};

export const PublicLayout = ({ children }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage } = useLanguage();
  const L = STR[language] || STR.tr;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#070b14] text-white antialiased selection:bg-sky-500/30" data-testid="public-site">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? 'bg-[#070b14]/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent border-b border-transparent'
        }`}
        data-testid="public-header"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="public-logo">
            <img src="/flowupy-logo.png" alt="FlowUpy" className="w-9 h-9 object-contain" />
            <span className="text-lg font-bold tracking-tight">FlowUpy</span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm font-medium text-slate-300 hover:text-white transition-colors" data-testid={`nav-${l.to.slice(1)}`}>
                {l.label[language]}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <button onClick={toggleLanguage} className="flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-full text-slate-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="public-language-toggle">
              <Globe className="w-4 h-4" /> {language === 'tr' ? 'EN' : 'TR'}
            </button>
            <Link to="/login" className="text-sm font-semibold px-4 py-2 rounded-full text-slate-200 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-login">
              {L.login}
            </Link>
            <Link to="/kayit" className="text-sm font-semibold px-5 py-2.5 rounded-full bg-sky-500 text-slate-950 hover:bg-sky-400 transition-colors shadow-lg shadow-sky-500/20" data-testid="nav-signup">
              {L.signup}
            </Link>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button onClick={toggleLanguage} className="flex items-center gap-1 text-sm font-semibold p-2 text-slate-200" data-testid="public-language-toggle-mobile">
              <Globe className="w-5 h-5" /> {language === 'tr' ? 'EN' : 'TR'}
            </button>
            <button className="p-2 text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="public-menu-toggle">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-[#070b14]/95 backdrop-blur-xl border-b border-white/10 px-5 py-4 space-y-2">
            {navLinks.map((l) => (
              <Link key={l.to} to={l.to} className="block text-sm font-medium py-2 text-slate-200">{l.label[language]}</Link>
            ))}
            <Link to="/login" className="block text-sm font-semibold py-2 text-slate-200">{L.login}</Link>
            <Link to="/kayit" className="block text-center text-sm font-semibold px-5 py-2.5 rounded-full bg-sky-500 text-slate-950">{L.signup}</Link>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <footer className="relative bg-[#0a0f1c] border-t border-white/10 mt-24 overflow-hidden" data-testid="public-footer">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-sky-500/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/flowupy-logo.png" alt="FlowUpy" className="w-9 h-9 object-contain" />
              <span className="text-lg font-bold">FlowUpy</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              {L.tagline}
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-xs uppercase tracking-widest text-slate-500">{L.product}</h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li><Link to="/ozellikler" className="hover:text-sky-400 transition-colors">{L.features}</Link></li>
              <li><Link to="/fiyatlandirma" className="hover:text-sky-400 transition-colors">{L.pricing}</Link></li>
              <li><Link to="/kayit" className="hover:text-sky-400 transition-colors">{L.freeDemo}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-xs uppercase tracking-widest text-slate-500">{L.legal}</h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li><Link to="/kvkk" className="hover:text-sky-400 transition-colors">{L.kvkk}</Link></li>
              <li><Link to="/gizlilik" className="hover:text-sky-400 transition-colors">{L.privacy}</Link></li>
              <li><Link to="/mesafeli-satis" className="hover:text-sky-400 transition-colors">{L.distance}</Link></li>
              <li><Link to="/iade" className="hover:text-sky-400 transition-colors">{L.refund}</Link></li>
              <li><Link to="/cerez" className="hover:text-sky-400 transition-colors">{L.cookie}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-xs uppercase tracking-widest text-slate-500">{L.contact}</h4>
            <ul className="space-y-2.5 text-sm text-slate-400">
              <li><Link to="/iletisim" className="hover:text-sky-400 transition-colors">{L.reach}</Link></li>
            </ul>
          </div>
        </div>
        <div className="relative border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
            <span>© {new Date().getFullYear()} FlowUpy. {L.rights}</span>
            <span className="flex items-center gap-1.5" data-testid="powered-by-datinova">
              {L.poweredBy}
              <a
                href="https://datinova.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 font-semibold text-slate-300 hover:text-sky-400 transition-colors"
                data-testid="datinova-link"
              >
                Datinova <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};
